"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Moon, Sun } from "lucide-react";

const DARK_MODE_VIDEO = "/Tuxito_v1.webm";
const LIGHT_MODE_VIDEO = "/Tuxito_v2.webm";

export default function HeroSection() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const sectionRef = useRef<HTMLElement | null>(null);
  const logoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const saved = window.localStorage.getItem("hero-dark-mode");
    setIsDarkMode(saved === "true");
  }, []);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      window.localStorage.setItem("hero-dark-mode", String(next));
      return next;
    });
  };

  useEffect(() => {
    const section = sectionRef.current;
    const logo = logoRef.current;
    if (!section || !logo) return;

    let rafId: number | null = null;

    const updateLogoPosition = () => {
      const sectionRect = section.getBoundingClientRect();
      const sectionHeight = sectionRect.height;
      if (sectionHeight <= 0) return;

      const heroBottom = sectionRect.bottom;
      const transitionStart = 220;
      const transitionEnd = 80;
      const rawNavProgress =
        (transitionStart - heroBottom) / (transitionStart - transitionEnd);
      const navProgress = Math.max(0, Math.min(1, rawNavProgress));

      const scale = 1 - navProgress * 0.82;
      const translateY = -navProgress * 210;
      const opacity = 1 - navProgress;

      logo.style.transform = `translateY(${translateY}px) scale(${scale})`;
      logo.style.opacity = `${opacity}`;
    };

    const requestUpdate = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateLogoPosition();
      });
    };

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    requestUpdate();

    return () => {
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  return (
    <section
      id="hero-section"
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden isolate"
    >
      <video
        className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-700 ${
          isDarkMode ? "opacity-100" : "opacity-0"
        }`}
        autoPlay
        loop
        muted
        playsInline
      >
        <source src={DARK_MODE_VIDEO} type="video/webm" />
      </video>

      <video
        className={`absolute inset-0 w-full h-full object-cover z-0 transition-opacity duration-700 ${
          isDarkMode ? "opacity-0" : "opacity-100"
        }`}
        autoPlay
        loop
        muted
        playsInline
      >
        <source src={LIGHT_MODE_VIDEO} type="video/webm" />
      </video>

      <button
        type="button"
        onClick={toggleDarkMode}
        className={`absolute top-24 right-6 z-20 w-10 h-10 rounded-full border backdrop-blur-sm flex items-center justify-center transition-colors ${
          isDarkMode
            ? "border-white/70 bg-black/20 text-white hover:bg-black/35"
            : "border-black/70 bg-white/60 text-black hover:bg-white/80"
        }`}
        aria-pressed={isDarkMode}
        aria-label={isDarkMode ? "Activer le mode clair" : "Activer le mode sombre"}
      >
        {isDarkMode ? <Moon size={16} /> : <Sun size={16} />}
      </button>

      <div ref={logoRef} className="relative z-10 w-[238px] md:w-[340px] lg:w-[442px] aspect-square mix-blend-difference will-change-transform transition-opacity duration-200">
        <Image
          src="/rares_corp.png"
          alt="Rares Corp"
          fill
          priority
          className="object-contain transition-all duration-500 brightness-0 invert"
        />
      </div>

      {/* Scroll indicator */}
      <div
        id="hero-scroll-indicator"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float"
      >
        <div
          className={`w-6 h-10 border-2 rounded-full flex items-start justify-center p-1.5 transition-colors ${
            isDarkMode ? "border-white" : "border-black"
          }`}
        >
          <div
            className={`w-1.5 h-3 rounded-full animate-bounce transition-colors ${
              isDarkMode ? "bg-white" : "bg-black"
            }`}
          />
        </div>
      </div>
    </section>
  );
}
